import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Plus, Edit2, Trash2, Download, Save, X, 
  BookOpen, Video, FileText, HelpCircle,
  ChevronDown, ChevronUp, GripVertical,
  Loader2, CheckCircle, AlertCircle
} from 'lucide-react';

const CATEGORIES = [
  'Safety & Compliance',
  'Clinical Skills',
  'Patient Care',
  'Documentation',
  'Communication',
  'Professional Development',
  'Company Policies'
];

export default function LMSAdminTraining() {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTraining, setEditingTraining] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Safety & Compliance',
    duration_minutes: 30,
    passing_score: 80,
    validity_days: 365,
    is_required: true,
    video_url: '',
    pdf_material_url: '',
    sections: [],
    questions: []
  });

  useEffect(() => {
    fetchTrainings();
  }, []);

  const fetchTrainings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/lms/admin/trainings');
      setTrainings(response.data);
    } catch (error) {
      toast.error('Failed to load trainings');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'Safety & Compliance',
      duration_minutes: 30,
      passing_score: 80,
      validity_days: 365,
      is_required: true,
      video_url: '',
      pdf_material_url: '',
      sections: [],
      questions: []
    });
    setEditingTraining(null);
  };

  const handleEdit = (training) => {
    setFormData({
      title: training.title,
      description: training.description || '',
      category: training.category,
      duration_minutes: training.duration_minutes || 30,
      passing_score: training.passing_score,
      validity_days: training.validity_days,
      is_required: training.is_required,
      video_url: training.video_url || '',
      pdf_material_url: training.pdf_material_url || '',
      sections: training.sections || [],
      questions: training.questions || []
    });
    setEditingTraining(training);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.category) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingTraining) {
        await api.put(`/lms/admin/trainings/${editingTraining.id}`, {
          title: formData.title,
          description: formData.description,
          category: formData.category,
          duration_minutes: formData.duration_minutes,
          passing_score: formData.passing_score,
          validity_days: formData.validity_days,
          is_required: formData.is_required,
          video_url: formData.video_url || null,
          pdf_material_url: formData.pdf_material_url || null
        });
        toast.success('Training updated successfully');
      } else {
        await api.post('/lms/admin/trainings', formData);
        toast.success('Training created successfully');
      }
      fetchTrainings();
      setShowForm(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save training');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (trainingId) => {
    if (!window.confirm('Are you sure you want to deactivate this training?')) return;
    
    try {
      await api.delete(`/lms/admin/trainings/${trainingId}`);
      toast.success('Training deactivated');
      fetchTrainings();
    } catch (error) {
      toast.error('Failed to delete training');
    }
  };

  const handleDownloadPdf = async (training) => {
    setDownloadingPdf(training.id);
    try {
      const response = await api.get(`/lms/admin/trainings/${training.id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${training.title.replace(/ /g, '_')}_Training_Packet.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Section Management
  const addSection = () => {
    setFormData({
      ...formData,
      sections: [...formData.sections, {
        title: '',
        content: '',
        order: formData.sections.length + 1,
        image_url: ''
      }]
    });
  };

  const updateSection = (index, field, value) => {
    const newSections = [...formData.sections];
    newSections[index] = { ...newSections[index], [field]: value };
    setFormData({ ...formData, sections: newSections });
  };

  const removeSection = (index) => {
    const newSections = formData.sections.filter((_, i) => i !== index);
    setFormData({ ...formData, sections: newSections });
  };

  // Question Management
  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [...formData.questions, {
        question: '',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        correct_answer: 'a',
        order: formData.questions.length + 1
      }]
    });
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setFormData({ ...formData, questions: newQuestions });
  };

  const removeQuestion = (index) => {
    const newQuestions = formData.questions.filter((_, i) => i !== index);
    setFormData({ ...formData, questions: newQuestions });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-purple-900">LMS Training Management</h2>
          <p className="text-gray-600">Create and manage training modules</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="add-training-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Training
        </Button>
      </div>

      {/* Training Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-purple-900">
                {editingTraining ? 'Edit Training' : 'Create New Training'}
              </h3>
              <Button variant="ghost" onClick={() => { setShowForm(false); resetForm(); }}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Training title"
                    data-testid="training-title-input"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full h-10 px-3 border rounded-md"
                    data-testid="training-category-select"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the training"
                  rows={3}
                  data-testid="training-description-input"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Duration (mins)</Label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 0 })}
                    min="1"
                  />
                </div>
                <div>
                  <Label>Passing Score (%)</Label>
                  <Input
                    type="number"
                    value={formData.passing_score}
                    onChange={(e) => setFormData({ ...formData, passing_score: parseInt(e.target.value) || 80 })}
                    min="1"
                    max="100"
                  />
                </div>
                <div>
                  <Label>Valid Days</Label>
                  <Input
                    type="number"
                    value={formData.validity_days}
                    onChange={(e) => setFormData({ ...formData, validity_days: parseInt(e.target.value) || 365 })}
                    min="1"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_required}
                      onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-sm">Required</span>
                  </label>
                </div>
              </div>

              {/* Media URLs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    Video URL
                  </Label>
                  <Input
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    PDF Material URL
                  </Label>
                  <Input
                    value={formData.pdf_material_url}
                    onChange={(e) => setFormData({ ...formData, pdf_material_url: e.target.value })}
                    placeholder="https://storage.../training.pdf"
                  />
                </div>
              </div>

              {/* Sections */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-lg font-semibold flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Content Sections
                  </Label>
                  <Button variant="outline" size="sm" onClick={addSection}>
                    <Plus className="w-4 h-4 mr-1" /> Add Section
                  </Button>
                </div>
                
                {formData.sections.map((section, index) => (
                  <Card key={index} className="p-4 mb-4 bg-gray-50">
                    <div className="flex items-start gap-2 mb-3">
                      <GripVertical className="w-5 h-5 text-gray-400 mt-2" />
                      <div className="flex-1 space-y-3">
                        <Input
                          value={section.title}
                          onChange={(e) => updateSection(index, 'title', e.target.value)}
                          placeholder={`Section ${index + 1} Title`}
                        />
                        <Textarea
                          value={section.content}
                          onChange={(e) => updateSection(index, 'content', e.target.value)}
                          placeholder="Section content (supports Markdown: ## Heading, - bullet, **bold**)"
                          rows={6}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSection(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Quiz Questions */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-lg font-semibold flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" />
                    Quiz Questions
                  </Label>
                  <Button variant="outline" size="sm" onClick={addQuestion}>
                    <Plus className="w-4 h-4 mr-1" /> Add Question
                  </Button>
                </div>
                
                {formData.questions.map((question, index) => (
                  <Card key={index} className="p-4 mb-4 bg-purple-50">
                    <div className="flex items-start gap-2">
                      <span className="font-bold text-purple-600">Q{index + 1}</span>
                      <div className="flex-1 space-y-3">
                        <Textarea
                          value={question.question}
                          onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                          placeholder="Question text"
                          rows={2}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 flex items-center justify-center rounded text-sm font-bold ${question.correct_answer === 'a' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>A</span>
                            <Input
                              value={question.option_a}
                              onChange={(e) => updateQuestion(index, 'option_a', e.target.value)}
                              placeholder="Option A"
                              className="flex-1"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 flex items-center justify-center rounded text-sm font-bold ${question.correct_answer === 'b' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>B</span>
                            <Input
                              value={question.option_b}
                              onChange={(e) => updateQuestion(index, 'option_b', e.target.value)}
                              placeholder="Option B"
                              className="flex-1"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 flex items-center justify-center rounded text-sm font-bold ${question.correct_answer === 'c' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>C</span>
                            <Input
                              value={question.option_c}
                              onChange={(e) => updateQuestion(index, 'option_c', e.target.value)}
                              placeholder="Option C"
                              className="flex-1"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 flex items-center justify-center rounded text-sm font-bold ${question.correct_answer === 'd' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>D</span>
                            <Input
                              value={question.option_d}
                              onChange={(e) => updateQuestion(index, 'option_d', e.target.value)}
                              placeholder="Option D"
                              className="flex-1"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Label className="text-sm">Correct Answer:</Label>
                          {['a', 'b', 'c', 'd'].map(opt => (
                            <label key={opt} className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="radio"
                                name={`correct-${index}`}
                                checked={question.correct_answer === opt}
                                onChange={() => updateQuestion(index, 'correct_answer', opt)}
                                className="text-purple-600"
                              />
                              <span className="text-sm font-medium">{opt.toUpperCase()}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeQuestion(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="save-training-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {editingTraining ? 'Update Training' : 'Create Training'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Training List */}
      <div className="grid gap-4">
        {trainings.length === 0 ? (
          <Card className="p-8 text-center">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No trainings created yet</p>
            <Button
              onClick={() => setShowForm(true)}
              variant="outline"
              className="mt-4"
            >
              Create your first training
            </Button>
          </Card>
        ) : (
          trainings.map(training => (
            <Card 
              key={training.id} 
              className={`p-4 border-l-4 ${training.is_active ? 'border-l-purple-500' : 'border-l-gray-300 opacity-60'}`}
              data-testid={`training-card-${training.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-purple-900">{training.title}</h3>
                    {!training.is_active && (
                      <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Inactive</span>
                    )}
                    {training.is_required && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Required</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{training.description}</p>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      {training.category}
                    </span>
                    <span>{training.duration_minutes} mins</span>
                    <span>Pass: {training.passing_score}%</span>
                    <span>{training.sections?.length || 0} sections</span>
                    <span>{training.questions?.length || 0} questions</span>
                    {training.video_url && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <Video className="w-3 h-3" /> Video
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownloadPdf(training)}
                    disabled={downloadingPdf === training.id}
                    title="Download PDF"
                    data-testid={`download-pdf-btn-${training.id}`}
                  >
                    {downloadingPdf === training.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(training)}
                    title="Edit"
                    data-testid={`edit-training-btn-${training.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  {training.is_active && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(training.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Deactivate"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
